import React, {useRef, useState, useEffect, useMemo} from 'react';
import moment from 'moment';
import ReactDOM from 'react-dom';
import {parseDate} from '@internationalized/date';
import {WEEK_PICKER_HEADER_TRIGGER_SELECTOR} from '../../../constants';
import {WeekPicker} from '@forecast-it/design-system';
import {ForecastLocaleProvider} from '@forecast-it/design-system/dist/locale/ForecastLocaleProvider';
import {Calendar} from '@forecast-it/design-system/dist/forecast-v2/components/controls/datepicker/supportcomponents/Calendar';
import {DatePickerDialog} from '@forecast-it/design-system/dist/forecast-v2/components/controls/datepicker/supportcomponents/DatePickerDialog';

interface TimeApprovalWeekPickerProps {
	currentViewingDate: moment.Moment;
	onWeekChange: (date: moment.Moment) => void;
	onOpen: () => void;
	calendarWeekIndicatorRenderer: (weekstart: string) => React.ReactNode;
	monthNavPreviousIndicatorRenderer: (currentMonth: Date) => React.ReactNode;
	monthNavNextIndicatorRenderer: (currentMonth: Date) => React.ReactNode;
	weekNavPreviousIndicatorRenderer: (currentWeek: Date) => React.ReactNode;
	weekNavNextIndicatorRenderer: (currentWeek: Date) => React.ReactNode;
}

export const TimeApprovalWeekPicker = ({
	currentViewingDate,
	onWeekChange,
	onOpen,
	calendarWeekIndicatorRenderer,
	monthNavPreviousIndicatorRenderer,
	monthNavNextIndicatorRenderer,
	weekNavPreviousIndicatorRenderer,
	weekNavNextIndicatorRenderer,
}: TimeApprovalWeekPickerProps): JSX.Element => {
	const wrapperRef = useRef<HTMLDivElement | null>(null);
	const dialogRef = useRef<HTMLDivElement | null>(null);
	const [isCalendarOpen, setIsCalendarOpen] = useState(false);
	const [calendarPosition, setCalendarPosition] = useState({top: 0, left: -24});
	const portalContainer = useMemo(
		() => (typeof document === 'undefined' ? null : document.querySelector('#root-portal-container')),
		[]
	);
	const currentWeekStart = currentViewingDate.clone().startOf('week').format('YYYY-MM-DD');

	const syncCalendarPosition = () => {
		const trigger = wrapperRef.current?.querySelector(WEEK_PICKER_HEADER_TRIGGER_SELECTOR);
		if (!trigger) {
			return;
		}

		const triggerRect = trigger.getBoundingClientRect();
		const dialogWidth = dialogRef.current?.offsetWidth || 0;
		const viewportPadding = 16;
		const triggerCenterX = triggerRect.left + triggerRect.width / 2;
		let left = dialogWidth ? triggerCenterX - dialogWidth / 2 : triggerCenterX;
		left = Math.max(viewportPadding, Math.min(left, window.innerWidth - (dialogWidth || 0) - viewportPadding));

		setCalendarPosition({
			top: triggerRect.bottom + 8,
			left,
		});
	};

	useEffect(() => {
		if (!isCalendarOpen) {
			return undefined;
		}

		const animationFrame = requestAnimationFrame(syncCalendarPosition);
		const handlePointerDown = (event: PointerEvent): void => {
			if (wrapperRef.current?.contains(event.target as Node) || dialogRef.current?.contains(event.target as Node)) {
				return;
			}
			setIsCalendarOpen(false);
		};
		const handleKeyDown = (event: KeyboardEvent): void => {
			if (event.key === 'Escape') {
				setIsCalendarOpen(false);
			}
		};
		const handleViewportChange = () => {
			syncCalendarPosition();
		};

		document.addEventListener('pointerdown', handlePointerDown, true);
		document.addEventListener('keydown', handleKeyDown, true);
		window.addEventListener('resize', handleViewportChange);
		window.addEventListener('scroll', handleViewportChange, true);

		return () => {
			cancelAnimationFrame(animationFrame);
			document.removeEventListener('pointerdown', handlePointerDown, true);
			document.removeEventListener('keydown', handleKeyDown, true);
			window.removeEventListener('resize', handleViewportChange);
			window.removeEventListener('scroll', handleViewportChange, true);
		};
	}, [isCalendarOpen, currentWeekStart]);

	const toggleCalendar = () => {
		if (!isCalendarOpen) {
			onOpen?.();
		}
		setIsCalendarOpen(isOpen => !isOpen);
	};

	const handleHeaderTriggerPointerDownCapture = event => {
		if (event.target.closest(WEEK_PICKER_HEADER_TRIGGER_SELECTOR)) {
			event.preventDefault();
			event.stopPropagation();
		}
	};

	const handleClickCapture = event => {
		if (event.target.closest(WEEK_PICKER_HEADER_TRIGGER_SELECTOR)) {
			event.preventDefault();
			event.stopPropagation();
			toggleCalendar();
		}
	};

	const handleKeyDownCapture = event => {
		if (event.target.closest(WEEK_PICKER_HEADER_TRIGGER_SELECTOR) && (event.key === 'Enter' || event.key === ' ')) {
			event.preventDefault();
			event.stopPropagation();
			toggleCalendar();
		}
	};

	return (
		<div
			ref={wrapperRef}
			onPointerDownCapture={handleHeaderTriggerPointerDownCapture}
			onClickCapture={handleClickCapture}
			onKeyDownCapture={handleKeyDownCapture}
		>
			<WeekPicker
				size="large"
				value={currentViewingDate.clone().startOf('week').format('YYYY-MM-DD')}
				onChange={startDate => onWeekChange(moment(startDate))}
				calendarWeekIndicatorRenderer={calendarWeekIndicatorRenderer}
				monthNavPreviousIndicatorRenderer={monthNavPreviousIndicatorRenderer}
				monthNavNextIndicatorRenderer={monthNavNextIndicatorRenderer}
				weekNavPreviousIndicatorRenderer={weekNavPreviousIndicatorRenderer}
				weekNavNextIndicatorRenderer={weekNavNextIndicatorRenderer}
				data-cy="time-approval-week-picker"
				aria-label="time-approval-week-picker"
			/>
			{isCalendarOpen && portalContainer
				? ReactDOM.createPortal(
						<div
							ref={dialogRef}
							style={{
								position: 'fixed',
								top: `${calendarPosition.top}px`,
								left: `${calendarPosition.left}px`,
								zIndex: 1000,
							}}
							data-cy="time-approval-week-picker-calendar"
						>
							<ForecastLocaleProvider>
								<DatePickerDialog ariaDialogProps={{'aria-label': 'Select week'}}>
									<Calendar
										value={parseDate(currentWeekStart)}
										focusedValue={parseDate(currentWeekStart)}
										onChange={calendarDate => {
											onWeekChange(moment(calendarDate.toString()).startOf('week'));
											setIsCalendarOpen(false);
										}}
										selectWholeWeek={true}
										highlightToday={false}
										calendarWeekIndicatorRenderer={calendarWeekIndicatorRenderer}
									/>
								</DatePickerDialog>
							</ForecastLocaleProvider>
						</div>,
						portalContainer
				  )
				: null}
		</div>
	);
};
